"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223305" className="flex gap-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-default font-medium tracking-tight text-foreground hover:text-primary transition-colors duration-200">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col py-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block px-6 py-4 text-base font-default font-medium tracking-tight text-foreground hover:text-primary hover:bg-primary/5 transition-colors" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md shadow-sm" : ""}`}>
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {}
          <Link to="/HomePage#" className="flex-shrink-0 z-50">
            <img src="https://werkys.com/wp-content/uploads/2026/04/mt_monogram_gold_logo.png" alt="Werkys" className="h-7 w-auto object-contain" />
          </Link>

          {}
          <div className="hidden md:flex items-center justify-center flex-1 px-8">
            <MenuProvider menu_id="33">
              <DesktopMenu />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4 z-50">
            <Button variant="ghost" className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold text-sm px-6 h-10 rounded-full transition-all duration-200"><a href='https://account.werkys.com/sign-up'>Get Started</a></Button>

            <Button variant="ghost" size="icon" className="md:hidden p-2 text-foreground hover:text-primary transition-colors rounded-none bg-transparent border-0 hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className={`md:hidden absolute top-full left-0 w-full bg-background border-t border-border/10 shadow-lg transition-all duration-300 origin-top overflow-hidden ${isMobileMenuOpen ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"}`}>
        <MenuProvider menu_id="33">
          <MobileMenu onItemClick={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
        <div className="p-6 pt-0">
          <Button variant="ghost" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold text-sm h-12 rounded-full transition-all duration-200" onClick={() => setIsMobileMenuOpen(false)}>
            Get Started
          </Button>
        </div>
      </div>
    </section>;
}